import { useEffect, useState } from "react";

type PermissionStatus = "granted" | "denied" | "prompt" | "unknown";

interface UseMicrophonePermissionReturn {
  permissionStatus: PermissionStatus;
  isRequestingPermission: boolean;
  requestPermission: () => Promise<void>;
  hasPermission: boolean;
}

export default function useMicrophonePermission(): UseMicrophonePermissionReturn {
  const [permissionStatus, setPermissionStatus] =
    useState<PermissionStatus>("unknown");
  const [isRequestingPermission, setIsRequestingPermission] = useState(false);

  useEffect(() => {
    (async () => {
      if (navigator.permissions) {
        const permission = await navigator.permissions.query({
          name: "microphone" as PermissionName,
        });
        setPermissionStatus(permission.state);

        permission.onchange = () => {
          setPermissionStatus(permission.state);
        };
      }
    })();
  }, []);

  async function requestPermission() {
    if (permissionStatus !== "granted") {
      setIsRequestingPermission(true);

      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });
        stream.getTracks().forEach((track) => track.stop());

        setPermissionStatus("granted");
      } catch (_error) {
        setPermissionStatus("denied");
        throw _error;
      } finally {
        setIsRequestingPermission(false);
      }
    }
  }

  const hasPermission = permissionStatus === "granted";

  return {
    permissionStatus,
    isRequestingPermission,
    requestPermission,
    hasPermission,
  };
}
